import { useCallback, useEffect, useRef } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { CORE_TOOLBAR_IDS, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { CORE_WINDOW_IDS, useDispatchWindows, useWindow, windowsActions } from "contexts/windows";
import ObjectTreeIcon from "media/icons/object-tree.svg?react";
import PropertiesIcon from "media/icons/properties.svg?react";

/**
 * Registers the details toolbar with core buttons (object tree, properties) and syncs
 * their active state with the corresponding window open state. Call once from the
 * component that owns the details toolbar (e.g. DetailsMenu).
 * No cleanup: toolbar state is left in context so SDK-added custom buttons persist.
 */
export function useDetailsToolbarRegistration(id = CORE_TOOLBAR_IDS.DETAILS) {
    const propertiesWindow = useWindow(CORE_WINDOW_IDS.PROPERTIES);
    const objectTreeWindow = useWindow(CORE_WINDOW_IDS.OBJECT_TREE);
    const dispatchWindows = useDispatchWindows();
    const dispatchToolbars = useDispatchToolbars();
    const toolbarsAddedRef = useRef(false);

    const handlePropertiesClick = useCallback(() => {
        dispatchWindows(
            windowsActions.toggleWindow(CORE_WINDOW_IDS.PROPERTIES, {
                elementId: id,
                placement: "right",
            }),
        );
        void track(Events.toolbar.propertiesPanelTapped, { origin: EventOrigin.Toolbar });
    }, [dispatchWindows, id]);

    const handleObjectTreeClick = useCallback(() => {
        dispatchWindows(
            windowsActions.toggleWindow(CORE_WINDOW_IDS.OBJECT_TREE, {
                elementId: id,
                placement: "right",
            }),
        );
        void track(Events.toolbar.objectTreeTapped, { origin: EventOrigin.Toolbar });
    }, [dispatchWindows, id]);

    useEffect(() => {
        if (toolbarsAddedRef.current) {
            return;
        }

        const buttons = [
            {
                config: {
                    id: "objectTree",
                    icon: <ObjectTreeIcon />,
                    onClick: handleObjectTreeClick,
                    title: "Show object tree",
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "properties",
                    icon: <PropertiesIcon />,
                    onClick: handlePropertiesClick,
                    title: "Show object properties",
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ];
        dispatchToolbars(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );
        toolbarsAddedRef.current = true;
        // Intentionally no cleanup: keep toolbar state so SDK custom buttons persist
    }, [dispatchToolbars, handlePropertiesClick, handleObjectTreeClick, id]);

    useEffect(() => {
        dispatchToolbars(
            toolbarsActions.bulkUpdateButtonStates(id, {
                objectTree: {
                    active: objectTreeWindow?.state.open ?? false,
                },
                properties: {
                    active: propertiesWindow?.state.open ?? false,
                },
            }),
        );
    }, [dispatchToolbars, id, objectTreeWindow?.state.open, propertiesWindow?.state.open]);
}
